import { useIntl } from 'react-intl';

/**
 * A hook that returns a function to translate a message ID, returning null if translation fails.
 *
 * NOTE: This file has special webpack/babel configuration to avoid formatjs static analysis errors.
 *
 * TECHNICAL EXPLANATION:
 * The babel-plugin-formatjs in our build pipeline attempts to statically analyze all formatMessage()
 * calls to extract message IDs for i18n compilation. However, this hook uses a dynamic ID parameter
 * (passed at runtime), which cannot be statically evaluated during build time.
 *
 * To resolve this, we've made the following build configuration adjustments:
 *
 * 1. BABEL CONFIG (client.babel.js & renderer.babel.js):
 *    - Added ignore pattern for useTranslateOrNull.ts to the formatjs plugin configuration
 *
 * 2. WEBPACK CONFIG (client.webpack.js & renderer.webpack.js):
 *    - Created a special rule for useTranslateOrNull.ts files that:
 *      a) Uses babel-loader with formatjs plugin filtered out
 *      b) Excludes this file from @wyw-in-js/webpack-loader processing
 *    - Modified the main TypeScript rule to exclude useTranslateOrNull.ts files
 *
 * This ensures:
 * - Build succeeds without static analysis errors
 * - Runtime functionality remains unchanged
 * - Other files continue normal i18n message extraction
 *
 * @returns A function that takes a message ID and returns the translated string or null
 */
export const useTranslateOrNull = () => {
  const intl = useIntl();

  return (id: string) => {
    const message = intl.messages[id];
    if (!message || message === 'null') {
      return null;
    }
    const translated = intl.formatMessage({ id });
    if (translated === id || !translated) {
      return null;
    }
    return translated;
  };
};
